// `value` prop on `input` should not be null
/* eslint-disable no-undefined */

import getStartEndDates from "src/utils/Company/Absences/getStartEndDates";
import { parseHourFromTimeString } from "src/utils/Company/Absences/parseHourFormat";
import {
  EPatternUnit,
  ICustomFieldDTO,
  FieldType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  CustomFieldValues,
  IAbsenceRequest,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

/**
 * Prepares default form values for an absence request form based on data provided by API.
 * This function transforms absence request data into a format suitable for form initialization,
 * handling different types of absence requests (day-based or hour-based) and custom fields.
 *
 * @param name - The name of the absence type to be used in the form.
 * @param customFields - Array of custom fields configuration that need to be included in the form.
 * @param absenceRequestData - Values from predefined fields in previously saved absence request.
 */
export default (
  name: string,
  customFields?: ICustomFieldDTO[],
  absenceRequestData?: IAbsenceRequest
) => {
  const {
    attachments,
    comment,
    absenceTimes = [],
    justification,
    type,
  } = absenceRequestData ?? {};

  const defaultAttachments = attachments?.map((attachment) => ({
    id: attachment.attachmentId,
  }));

  const customFieldsValues = prepareCustomFieldsDefaultValues(
    customFields,
    absenceRequestData?.customFieldValues
  );

  return {
    absenceType: name,
    unit: type || EPatternUnit.Day,
    dates:
      type === EPatternUnit.Day ? getStartEndDates(absenceTimes) : [null, null],
    date: type === EPatternUnit.Hour ? new Date(absenceTimes[0]?.date) : null,
    startTime:
      type === EPatternUnit.Hour
        ? parseHourFromTimeString(absenceTimes[0]?.startTime)
        : 0,
    endTime:
      type === EPatternUnit.Hour
        ? parseHourFromTimeString(absenceTimes[0]?.endTime)
        : 0,
    approver: absenceRequestData?.approver || undefined,
    replacer: absenceRequestData?.replacer || undefined,
    justification: justification || undefined,
    comment: comment || undefined,
    attachments: attachments?.length ? defaultAttachments : [],
    ...customFieldsValues,
  };
};

/**
 * Prepares default values for custom fields based on their configuration.
 * This function transforms custom field definitions into form-ready default values,
 * handling different field types appropriately.
 *
 * @param customFields - Array of custom field configurations
 * @param existingValues - Optional existing values from a saved absence request
 * @returns Object with custom field IDs as keys and their default values
 */
export const prepareCustomFieldsDefaultValues = (
  customFields?: ICustomFieldDTO[],
  existingValues?: CustomFieldValues
): Record<string, string | string[] | boolean | Date | undefined> => {
  if (!customFields) return {};

  return customFields.reduce<
    Record<string, string | string[] | boolean | Date | undefined>
  >((acc, field) => {
    const { customFieldId, multipleChoice, fieldType, required } = field;
    const id = `customField_${customFieldId}`;
    const fieldValue = existingValues?.[customFieldId!];

    if (multipleChoice) {
      acc[id] = Array.isArray(fieldValue) ? fieldValue : [];
    } else if (fieldType === FieldType.CHOICE_SELECTION) {
      acc[id] = (fieldValue as string[])?.[0] || "";
    } else if (fieldType === FieldType.CHECKBOX) {
      acc[id] = fieldValue || false;
    } else if (fieldType === FieldType.DATE) {
      acc[id] = fieldValue ? new Date(fieldValue as string) : undefined;
    } else {
      acc[id] = required ? fieldValue || "" : fieldValue || undefined;
    }

    return acc;
  }, {});
};
